import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Calendar, Filter, RefreshCw } from 'lucide-react';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGES = [
  { key: 'weekly', label: 'Weekly (7d)', days: 7 },
  { key: 'monthly', label: 'Monthly (30d)', days: 30 },
  { key: 'quarterly', label: 'Quarterly (90d)', days: 90 },
];

/**
 * ActivityTrendsCharts — two side-by-side line charts:
 *   1. Appointments Scheduled vs Consultations Completed
 *   2. Calls Made vs Opportunities Created
 * Both charts share the same filters (date range + rep).
 */
export default function ActivityTrendsCharts({ reps = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState('weekly');
  const [repId, setRepId] = useState('all');

  const isoDaysAgo = useCallback((days) => {
    const d = new Date();
    d.setDate(d.getDate() - days + 1);
    return d.toISOString().split('T')[0];
  }, []);
  const todayISO = () => new Date().toISOString().split('T')[0];

  const fetchTrends = useCallback(async () => {
    try {
      setLoading(true);
      const range = RANGES.find(r => r.key === rangeKey) || RANGES[0];
      const params = new URLSearchParams({
        start_date: isoDaysAgo(range.days),
        end_date: todayISO(),
      });
      if (repId && repId !== 'all') params.append('rep_id', repId);
      const res = await fetch(`${API_URL}/api/intelligence/activity-trends?${params}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (err) {
      logger.error('Activity trends fetch error', err);
    } finally {
      setLoading(false);
    }
  }, [rangeKey, repId, isoDaysAgo]);

  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  const chart1 = data?.appointments_vs_consultations || [];
  const chart2 = data?.calls_vs_opportunities || [];
  const totals = data?.totals || {};

  const axisProps = useMemo(() => ({
    tick: { fontSize: 11, fill: '#64748b' },
    axisLine: { stroke: '#e2e8f0' },
    tickLine: { stroke: '#e2e8f0' },
  }), []);

  return (
    <div className="space-y-4" data-testid="activity-trends-section">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl border border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-700">Activity Trends</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={rangeKey} onValueChange={setRangeKey}>
            <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="trends-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={repId} onValueChange={setRepId}>
            <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="trends-rep-select">
              <Filter className="h-3 w-3 mr-1 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reps</SelectItem>
              {reps.map(r => <SelectItem key={r.user_id} value={r.user_id}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={fetchTrends} disabled={loading} data-testid="trends-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Chart 1: Appointments Scheduled vs Consultations Completed */}
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="chart-appointments-consultations">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Appointments Scheduled vs Consultations Completed</h4>
              <p className="text-xs text-slate-500 mt-0.5">Daily booking and completion volume</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5 text-slate-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  <span>{totals.appointments_scheduled ?? 0} appts</span>
                </span>
                <span className="flex items-center gap-1.5 text-slate-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span>{totals.consultations_completed ?? 0} consults</span>
                </span>
              </div>
            </div>
          </div>
          <div className="h-[280px] w-full" style={{ minHeight: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart1} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line type="monotone" dataKey="appointments_scheduled" name="Appointments Scheduled" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="consultations_completed" name="Consultations Completed" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 2: Calls Made vs Opportunities Created */}
        <div className="bg-white rounded-xl border border-slate-100 p-5" data-testid="chart-calls-opportunities">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-900">Calls Made vs Opportunities Created</h4>
              <p className="text-xs text-slate-500 mt-0.5">Outreach volume vs qualified opportunity creation</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5 text-slate-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-violet-500" />
                  <span>{totals.calls_made ?? 0} calls</span>
                </span>
                <span className="flex items-center gap-1.5 text-slate-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span>{totals.opportunities_created ?? 0} opps</span>
                </span>
              </div>
            </div>
          </div>
          <div className="h-[280px] w-full" style={{ minHeight: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chart2} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" {...axisProps} />
                <YAxis allowDecimals={false} {...axisProps} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                <Line type="monotone" dataKey="calls_made" name="Calls Made" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="opportunities_created" name="Opportunities Created" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
